import { useState } from 'react'

import { useModal } from '@context/ModalsContext'

import { FileData } from '@ui/components/FileUpload/types'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { CreateClientTest } from '@model/CreateClientTest'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { DocumentPropertyType } from '@model/DocumentPropertyType'

import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'

export interface UploadOptions {
  categoryId: DocumentCategoryType
  typeId: DocumentCategoryOptionType
  ticketIdOrTestId: string
  isTicketId?: boolean
}

export interface SubmissionValues {
  selectedAnswers?: Record<string, any>
  freeAnswers?: Record<string, string>
  category?: string
  testId?: string
  dateTaken?: string
  [key: string]: any
}

export interface UploadValues {
  documents?: Record<string, FileData[]> | FileData[]
  [key: string]: any
}

export const useClientTestSubmission = () => {
  const apiClient = useApiClient(ClientApiClient)
  const [isLoading, setLoading] = useState(false)
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)

  const submitTest = async (
    values: SubmissionValues,
    params: { clientId: string; category?: string; testId?: string }
  ) => {
    const selectedAnswers = Object.values(values.selectedAnswers || {}).flatMap((value) => value)

    const freeAnswers = Object.entries(values.freeAnswers || {}).map(([key, value]) => ({
      testQuestionId: key,
      answer: value,
    }))

    const payload: CreateClientTest = {
      clientId: params.clientId,
      category: params.category || values.category || '',
      testId: params.testId || values.testId || '',
      dateTaken: values.dateTaken || new Date().toISOString(),
      selectedAnswers,
      freeAnswers,
    }

    return await apiClient.createClientTests(payload)
  }

  const uploadDocuments = async (values: UploadValues, options: UploadOptions) => {
    const docs = values.documents

    if (!docs) return

    let files: (FileData & { questionId?: string })[]

    if (Array.isArray(docs)) {
      files = docs.filter((x) => !!x.fileName)
    } else {
      files = Object.entries(docs)
        .map(([questionId, filesData]) => filesData.map((x) => ({ ...x, questionId })).flat())
        .flat()
        .filter((x) => !!x.fileName)
    }

    if (files.length === 0) return

    const propertyKey = options.isTicketId
      ? DocumentPropertyType.TicketId
      : DocumentPropertyType.ClientTestId

    try {
      await apiClient.uploadDocuments({
        documents: files.map((file) => {
          const properties: Record<string, string> = {
            [propertyKey]: options.ticketIdOrTestId,
          }
          if (file.questionId) {
            properties[DocumentPropertyType.TestQuestionId] = file.questionId
          }

          return {
            categoryId: options.categoryId,
            typeId: options.typeId,
            file: file.base64Content,
            filename: file.fileName,
            properties,
          }
        }),
      })
    } catch (error) {
      alreadyUploadedModal.open()
      console.error('Document upload failed', error)
    }
  }

  const performSubmission = async <T>(
    action: () => Promise<T>,
    onError?: (error: any) => void
  ): Promise<T | undefined> => {
    setLoading(true)
    try {
      return await action()
    } catch (error: any) {
      if (onError) {
        onError(error)
      } else {
        const code = error?.response?.response?.data?.code
        if (code) {
          alreadyUploadedModal.open({ message: code })
        }
      }
      console.error(error)
      return undefined
    } finally {
      setLoading(false)
    }
  }

  return {
    isLoading,
    setLoading,
    submitTest,
    uploadDocuments,
    performSubmission,
  }
}
